import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Users, UserPlus, Crown, Activity, CreditCard, XCircle, Trash2, RefreshCw, Loader2, Search, ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="bg-brand-card border border-brand-border rounded-2xl p-4" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <div className="flex items-center gap-2 text-brand-ink3 mb-1.5">
            <Icon size={15} strokeWidth={1.8} className={accent || ''} />
            <span className="text-xs">{label}</span>
        </div>
        <p className="font-serif text-3xl text-brand-ink leading-none">{value ?? 0}</p>
    </div>
);

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso.endsWith && (iso.endsWith('Z') || iso.includes('+')) ? iso : iso + 'Z');
    if (isNaN(d)) return '—';
    return d.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' }) + ' ' +
        d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const providerBadge = (p) => {
    const map = { google: 'bg-blue-100 text-blue-700', email: 'bg-emerald-100 text-emerald-700', magic: 'bg-purple-100 text-purple-700', guest: 'bg-gray-100 text-gray-600' };
    return map[p] || 'bg-gray-100 text-gray-600';
};

// ---- Users browser ----
function UsersBrowser() {
    const [q, setQ] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [role, setRole] = useState('');
    const [sort, setSort] = useState('created_at');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ total: 0, rows: [], page: 1, page_size: 25 });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const r = await api.get('/admin/users', { params: { q, date_from: from, date_to: to, role, sort, page: p, page_size: 25 } });
            setData(r.data);
            setPage(r.data.page);
        } catch { toast.error('Could not load users'); }
        finally { setLoading(false); }
    }, [q, from, to, role, sort]);

    useEffect(() => { load(1); }, [load]);

    const search = (e) => { e?.preventDefault(); load(1); };
    const reset = () => { setQ(''); setFrom(''); setTo(''); setRole(''); setSort('created_at'); setTimeout(() => load(1), 0); };
    const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 25)));

    return (
        <section className="mb-10" data-testid="users-browser">
            <h2 className="text-xs uppercase tracking-wider text-brand-ink3 mb-2 flex items-center gap-1.5"><Users size={13} /> Users &amp; logins</h2>

            <form onSubmit={search} className="bg-brand-card border border-brand-border rounded-2xl p-3 space-y-2.5 mb-3">
                <div className="relative">
                    <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-ink3" />
                    <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="w-full bg-brand-bg2 border border-brand-border rounded-full pl-10 pr-4 py-2.5 text-sm text-brand-ink" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">From</span>
                        <input type="date" data-testid="users-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-2.5 py-2 text-sm text-brand-ink" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">To</span>
                        <input type="date" data-testid="users-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-2.5 py-2 text-sm text-brand-ink" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Role</span>
                        <select data-testid="users-role" value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-2.5 py-2 text-sm text-brand-ink">
                            <option value="">All</option>
                            <option value="registered">Registered</option>
                            <option value="guest">Guests</option>
                            <option value="premium">Premium</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Sort by</span>
                        <select data-testid="users-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-2.5 py-2 text-sm text-brand-ink">
                            <option value="created_at">Newest</option>
                            <option value="last_seen_at">Last seen</option>
                        </select>
                    </label>
                </div>
                <div className="flex gap-2">
                    <button type="submit" data-testid="users-apply" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-2.5 rounded-full text-sm font-medium">
                        <Search size={14} /> Apply
                    </button>
                    <button type="button" onClick={reset} className="px-4 py-2.5 rounded-full text-sm bg-brand-bg2 border border-brand-border text-brand-ink2">Reset</button>
                </div>
            </form>

            <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs text-brand-ink3">{data.total} result{data.total === 1 ? '' : 's'}</span>
                {loading && <Loader2 size={14} className="animate-spin text-brand-ink3" />}
            </div>

            <div className="bg-brand-card border border-brand-border rounded-2xl divide-y divide-brand-border overflow-hidden" data-testid="users-list">
                {data.rows.length === 0 && !loading && <p className="text-sm text-brand-ink3 p-4">No users match these filters.</p>}
                {data.rows.map((u) => (
                    <div key={u.user_id} className="flex items-center gap-3 p-3" data-testid={`user-row-${u.user_id}`}>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-brand-ink truncate">{u.name || u.email || 'Guest'}</span>
                                {u.premium && <Crown size={13} className="text-amber-500 flex-shrink-0" />}
                                {u.is_admin && <span className="text-[9px] uppercase font-bold text-brand-green">admin</span>}
                            </div>
                            <div className="text-[11px] text-brand-ink3 truncate">{u.email || '—'}</div>
                        </div>
                        <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded-full ${providerBadge(u.auth_provider)}`}>{u.auth_provider || '—'}</span>
                        <div className="text-right flex-shrink-0">
                            <div className="text-[11px] text-brand-ink2">{fmtDate(u.created_at)}</div>
                            <div className="text-[10px] text-brand-ink3">seen {fmtDate(u.last_seen_at)}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between mt-3">
                <button onClick={() => load(page - 1)} disabled={page <= 1 || loading} data-testid="users-prev" className="inline-flex items-center gap-1 text-sm text-brand-ink2 disabled:opacity-30 px-3 py-1.5"><ChevronLeft size={15} /> Prev</button>
                <span className="text-xs text-brand-ink3">Page {data.page} / {totalPages}</span>
                <button onClick={() => load(page + 1)} disabled={page >= totalPages || loading} data-testid="users-next" className="inline-flex items-center gap-1 text-sm text-brand-ink2 disabled:opacity-30 px-3 py-1.5">Next <ChevronRight size={15} /></button>
            </div>
        </section>
    );
}

// ---- Beta sign-ups browser ----

export default function AdminStats() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/stats');
            setData(r.data);
        } catch {
            toast.error('Could not load statistics');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (user && !user.is_admin) { navigate('/'); return; }
        if (user) load();
    }, [user, load, navigate]);

    if (!user || !user.is_admin) return null;

    const t = data?.totals || {};
    const g = data?.growth || {};
    const s = data?.subscriptions || {};
    const e = data?.events || {};

    return (
        <div className="px-5 pt-10 pb-32 max-w-3xl mx-auto" data-testid="admin-stats">
            <button data-testid="admin-stats-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Admin</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 inline-flex items-center gap-2"><BarChart3 size={26} /> User statistics</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/admin/links" data-testid="nav-admin-links" className="inline-flex items-center gap-1.5 text-sm bg-brand-card border border-brand-border rounded-full px-3 py-2 text-brand-ink hover:bg-brand-bg2"><Link2 size={14} /> Links</Link>
                    <button data-testid="stats-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-sm bg-brand-card border border-brand-border rounded-full px-3 py-2 text-brand-ink hover:bg-brand-bg2 disabled:opacity-50">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
                    </button>
                </div>
            </div>

            {loading && !data ? (
                <div className="flex justify-center py-20 text-brand-ink3"><Loader2 className="animate-spin" /></div>
            ) : (
                <>
                    <h2 className="text-xs uppercase tracking-wider text-brand-ink3 mb-2">Users</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <StatCard icon={Users} label="Total users" value={t.users} />
                        <StatCard icon={Users} label="Guests" value={t.guests} />
                        <StatCard icon={UserPlus} label="Registered" value={t.registered} />
                        <StatCard icon={Crown} label="Premium" value={t.premium} accent="text-amber-500" />
                    </div>

                    <h2 className="text-xs uppercase tracking-wider text-brand-ink3 mb-2">Growth &amp; activity</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <StatCard icon={UserPlus} label="New today" value={g.new_today} />
                        <StatCard icon={UserPlus} label="New this week" value={g.new_this_week} />
                        <StatCard icon={Activity} label="Active 7d" value={g.active_7d} />
                        <StatCard icon={Activity} label="Active 30d" value={g.active_30d} />
                    </div>

                    <h2 className="text-xs uppercase tracking-wider text-brand-ink3 mb-2">Subscriptions &amp; events</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                        <StatCard icon={CreditCard} label="Subs created" value={s.created} accent="text-green-600" />
                        <StatCard icon={XCircle} label="Subs cancelled" value={s.cancelled} accent="text-red-500" />
                        <StatCard icon={Crown} label="Active premium" value={s.active_premium} accent="text-amber-500" />
                        <StatCard icon={Trash2} label="Accounts deleted" value={e.accounts_deleted} accent="text-red-500" />
                    </div>

                    <UsersBrowser />

                    <h2 className="text-xs uppercase tracking-wider text-brand-ink3 mb-2">Recent activity</h2>
                    <div className="bg-brand-card border border-brand-border rounded-2xl divide-y divide-brand-border" data-testid="recent-events">
                        {(data?.recent_events || []).length === 0 && (
                            <p className="text-sm text-brand-ink3 p-4">No events recorded yet.</p>
                        )}
                        {(data?.recent_events || []).map((ev, i) => (
                            <div key={`${ev.created_at}-${i}`} className="flex items-center justify-between gap-3 p-3">
                                <span className="text-sm text-brand-ink truncate">{ev.label} · <span className="text-brand-ink3">{ev.who || 'Unknown'}</span></span>
                                <span className="text-xs text-brand-ink3 shrink-0">{fmtDate(ev.created_at)}</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-brand-ink3 mt-4">Founder gets instant emails for registrations, subscriptions, cancellations &amp; deletions, plus a daily 08:05 summary at benjamin@growdaily.fun.</p>
                </>
            )}
        </div>
    );
}
